/*
  Movrano – kleine Erweiterungen zu Dawn.
  Alles hier ist optional: ohne dieses Skript bleibt die Seite vollständig nutzbar.
  Stand: 25.10.2026
*/

if (!customElements.get('movrano-marquee')) {
  customElements.define(
    'movrano-marquee',
    class MovranoMarquee extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.movrano-marquee__toggle');
        if (!this.toggle) return;
        this.onToggle = this.onToggle.bind(this);
        this.toggle.addEventListener('click', this.onToggle);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.onToggle);
      }

      onToggle() {
        const paused = this.classList.toggle('is-paused');
        this.toggle.setAttribute('aria-pressed', String(paused));
        this.toggle.setAttribute(
          'aria-label',
          paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause
        );
      }
    }
  );
}

if (!customElements.get('movrano-sticky-atc')) {
  customElements.define(
    'movrano-sticky-atc',
    class MovranoStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('[data-mv-button]');
        this.label = this.querySelector('[data-mv-label]');
        this.priceTarget = this.querySelector('[data-mv-price]');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.showOnDesktop = this.hasAttribute('data-desktop');
        this.mainPassed = false;

        this.onClick = this.onClick.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.button.addEventListener('click', this.onClick);
        this.mobileQuery.addEventListener('change', this.updateVisibility);
        // Bewusst Scroll statt IntersectionObserver: Dawn tauscht den Kaufbutton beim Variantenwechsel aus,
        // danach meldete der Observer sich nicht mehr zuverlässig.
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        this.scheduleSync = this.scheduleSync.bind(this);
        const root = this.closest('product-info') || document.body;
        this.mutationObserver = new MutationObserver((mutations) => {
          if (mutations.some((m) => !this.contains(m.target))) this.scheduleSync();
        });
        this.mutationObserver.observe(root, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });

        this.sync();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.mobileQuery?.removeEventListener('change', this.updateVisibility);
        this.mutationObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.syncFrame = null;
        this.scrollFrame = null;
        document.body.classList.remove('movrano-sticky-visible');
      }

      getMainButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      onClick() {
        const mainButton = this.getMainButton();
        if (!mainButton || mainButton.disabled) return;
        mainButton.click();
      }

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.sync();
        });
      }

      // Übernimmt Preis und Button-Zustand aus dem echten Kaufbereich (Variante, Verfügbarkeit).
      sync() {
        const mainButton = this.getMainButton();
        if (!mainButton) {
          this.hidden = true;
          return;
        }

        const disabled = mainButton.hasAttribute('disabled');
        this.button.toggleAttribute('disabled', disabled);
        const mainLabel = mainButton.querySelector('span:not(.loading__spinner):not(.visually-hidden)');
        if (mainLabel && mainLabel.textContent.trim()) this.label.textContent = mainLabel.textContent.trim();

        const price = document.getElementById(`price-${this.sectionId}`);
        if (price && price.innerHTML !== this.lastPriceHtml) {
          this.lastPriceHtml = price.innerHTML;
          this.priceTarget.innerHTML = price.innerHTML;
          this.priceTarget.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        }

        this.measure();
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.measure();
        });
      }

      // Leiste zeigen, sobald der echte Kaufbutton oberhalb des sichtbaren Bereichs liegt.
      measure() {
        const mainButton = this.getMainButton();
        this.mainPassed = Boolean(mainButton && mainButton.getBoundingClientRect().bottom < 0);
        this.updateVisibility();
      }

      updateVisibility() {
        const show = (this.mobileQuery.matches || this.showOnDesktop) && this.mainPassed;
        this.hidden = !show;
        document.body.classList.toggle('movrano-sticky-visible', show);
      }
    }
  );
}

// Smartphone-Galerie: Punkte statt „1 / 6“. Ohne JS bleibt Dawns Zähler sichtbar.
function movranoInitGalleryDots(root = document) {
  root.querySelectorAll('.movrano-dots slider-component[id^="GalleryViewer"]').forEach((slider) => {
    if (slider.dataset.mvDots) return;
    const track = slider.querySelector('.product__media-list');
    if (!track) return;
    slider.dataset.mvDots = 'true';

    const dots = document.createElement('div');
    dots.className = 'movrano-gallery-dots';
    slider.appendChild(dots);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const visibleItems = () =>
      Array.from(track.querySelectorAll('.product__media-item')).filter((item) => item.clientWidth > 0);

    let items = [];
    const update = () => {
      const current = visibleItems();
      if (current.length !== items.length || current.some((item, i) => item !== items[i])) {
        items = current;
        dots.replaceChildren(
          ...items.map((item, i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'movrano-gallery-dots__dot';
            button.setAttribute('aria-label', `Bild ${i + 1} von ${items.length}`);
            button.addEventListener('click', () =>
              track.scrollTo({ left: item.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' })
            );
            return button;
          })
        );
        dots.hidden = items.length < 2;
      }
      const index = track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
      dots.querySelectorAll('button').forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
    };

    let frame = null;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        update();
      });
    };
    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    new MutationObserver(schedule).observe(track, { childList: true, attributes: true, attributeFilter: ['class'], subtree: true });

    update();
    slider.closest('.movrano-dots')?.classList.add('movrano-dots-ready');
  });
}

// Lieferfenster ab heute (optional nur Werktage). Ohne JS steht „3 und 5 Werktagen“ da.
function movranoInitDelivery(root = document) {
  root.querySelectorAll('[data-mv-delivery]').forEach((el) => {
    const min = Number(el.dataset.min);
    const max = Number(el.dataset.max);
    const skipWeekends = el.dataset.skipWeekends === 'true';
    const addDays = (days) => {
      const date = new Date();
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        if (skipWeekends && (date.getDay() === 0 || date.getDay() === 6)) continue;
        added++;
      }
      return date;
    };
    const format = new Intl.DateTimeFormat(document.documentElement.lang || 'de', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    el.querySelector('[data-mv-delivery-from]').textContent = format.format(addDays(min));
    el.querySelector('[data-mv-delivery-to]').textContent = `${format.format(addDays(max))}.`;
  });
}

// Lange Wörter im Produkttitel: verkleinert die Schrift nur so weit, dass jedes Wort in eine Zeile passt
// (für Browser ohne deutsche Silbentrennung). Ohne JS greift der normale Zeilenumbruch.
function movranoFitTitles(root = document) {
  root.querySelectorAll('.movrano-product .product__title h1').forEach((title) => {
    title.style.fontSize = '';
    const words = title.textContent.trim().split(/\s+/);
    if (!words.length) return;
    const probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;left:-9999px;';
    title.appendChild(probe);
    const widest = () =>
      Math.max(
        ...words.map((word) => {
          probe.textContent = word;
          return probe.getBoundingClientRect().width;
        })
      );
    const minSize = 18;
    let size = parseFloat(getComputedStyle(title).fontSize);
    // 2px Puffer gegen Rundungsunterschiede
    while (widest() > title.clientWidth - 2 && size > minSize) {
      size -= 1;
      title.style.fontSize = `${size}px`;
    }
    probe.remove();
  });
}

let movranoFitFrame = null;
window.addEventListener('resize', () => {
  cancelAnimationFrame(movranoFitFrame);
  movranoFitFrame = requestAnimationFrame(() => movranoFitTitles());
});

function movranoInit(root = document) {
  movranoInitGalleryDots(root);
  movranoInitDelivery(root);
  movranoFitTitles(root);
  document.fonts?.ready.then(() => movranoFitTitles(root));

  // Dawn tauscht beim Variantenwechsel Teile der Galerie aus: neue Galerie erneut ausstatten.
  root.querySelectorAll('.movrano-dots').forEach((product) => {
    if (product.dataset.mvWatch) return;
    product.dataset.mvWatch = 'true';
    new MutationObserver(() => movranoInitGalleryDots(product)).observe(product, { childList: true, subtree: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => movranoInit());
} else {
  movranoInit();
}
document.addEventListener('shopify:section:load', (event) => movranoInit(event.target));

// Shopify-Geldformat nachbilden ({{amount}}, {{amount_with_comma_separator}} …), damit JS-Preise wie Liquid aussehen.
function movranoFormatMoney(cents, format) {
  const value = Number(cents) || 0;
  const withDelimiters = (number, precision, thousands, decimal) => {
    const [whole, fraction] = (number / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(value, 2, "'", '.');
      case 'amount_with_space_separator':
        return withDelimiters(value, 2, ' ', ',');
      default:
        return withDelimiters(value, 2, ',', '.');
    }
  });
}

if (!customElements.get('movrano-bundle')) {
  customElements.define(
    'movrano-bundle',
    class MovranoBundle extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat;
        this.button = this.querySelector('[data-mv-bundle-add]');
        this.error = this.querySelector('.movrano-bundle__error');
        this.onChange = this.update.bind(this);
        this.onAdd = this.add.bind(this);
        this.addEventListener('change', this.onChange);
        this.button?.addEventListener('click', this.onAdd);
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.button?.removeEventListener('click', this.onAdd);
      }

      selectedVariant(item) {
        const input = item.querySelector('[data-mv-bundle-variant]');
        if (!input) return null;
        const source = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
        return {
          id: Number(input.value),
          price: Number(source.dataset.price),
          compare: Number(source.dataset.compare),
        };
      }

      checkedItems() {
        return Array.from(this.querySelectorAll('[data-mv-bundle-item]')).filter(
          (item) => item.querySelector('[data-mv-bundle-check]')?.checked
        );
      }

      update() {
        let total = 0;
        let compareTotal = 0;
        this.querySelectorAll('[data-mv-bundle-item]').forEach((item) => {
          const variant = this.selectedVariant(item);
          if (!variant) return;
          item.querySelector('[data-mv-bundle-price]').textContent = movranoFormatMoney(variant.price, this.moneyFormat);
          item.querySelector('[data-mv-bundle-compare-price]').textContent =
            variant.compare > variant.price ? movranoFormatMoney(variant.compare, this.moneyFormat) : '';
        });
        const checked = this.checkedItems();
        checked.forEach((item) => {
          const variant = this.selectedVariant(item);
          total += variant.price;
          compareTotal += Math.max(variant.compare, variant.price);
        });
        this.querySelector('[data-mv-bundle-total]').textContent = movranoFormatMoney(total, this.moneyFormat);
        this.querySelector('[data-mv-bundle-compare]').textContent =
          compareTotal > total ? movranoFormatMoney(compareTotal, this.moneyFormat) : '';
        if (this.button) this.button.disabled = checked.length === 0;
      }

      async add() {
        const items = this.checkedItems().map((item) => ({ id: this.selectedVariant(item).id, quantity: 1 }));
        if (!items.length) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart && this.dataset.cartType !== 'page') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        this.button.setAttribute('aria-disabled', 'true');
        this.button.classList.add('loading');
        this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
        this.error.hidden = true;

        try {
          const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const state = await response.json();
          if (!response.ok || state.status) {
            this.error.textContent = state.description || state.message || 'Fehler beim Hinzufügen.';
            this.error.hidden = false;
            return;
          }
          if (!cart || this.dataset.cartType === 'page') {
            window.location = this.dataset.cartUrl;
            return;
          }
          const first = state.items?.[0] || {};
          cart.classList.remove('is-empty');
          cart.renderContents({ ...state, key: first.key, id: first.id });
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'movrano-bundle', cartData: state });
          }
        } catch (error) {
          this.error.textContent = 'Fehler beim Hinzufügen. Bitte versuche es erneut.';
          this.error.hidden = false;
        } finally {
          this.button.removeAttribute('aria-disabled');
          this.button.classList.remove('loading');
          this.button.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }
    }
  );
}

// Logo-Leiste: Punkte und optionales automatisches Weiterblättern auf dem Smartphone.
if (!customElements.get('movrano-logo-slider')) {
  customElements.define(
    'movrano-logo-slider',
    class MovranoLogoSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.movrano-logo-list__track');
        this.dots = this.querySelector('.movrano-logo-list__dots');
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.paused = false;

        this.render = this.render.bind(this);
        this.onScroll = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.markActive());
        };
        this.pause = () => {
          this.paused = true;
          this.targetPage = null; // Nutzer übernimmt: danach wieder von der echten Position aus weiterblättern
        };
        this.resume = () => (this.paused = false);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.mobile.addEventListener('change', this.render);
        ['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
          this.addEventListener(type, this.pause, { passive: true })
        );
        ['pointerleave', 'focusout'].forEach((type) => this.addEventListener(type, this.resume));

        this.render();
        this.startAutoplay();
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        this.mobile?.removeEventListener('change', this.render);
      }

      items() {
        return Array.from(this.track.children);
      }

      step() {
        const items = this.items();
        return items[1] ? items[1].offsetLeft - items[0].offsetLeft : items[0]?.offsetWidth || 1;
      }

      // Anzahl möglicher Startpositionen (bei zwei sichtbaren Logos: Logos − 1)
      pageCount() {
        const items = this.items();
        if (!items.length) return 0;
        const visible = Math.max(1, Math.round((this.track.clientWidth - 1) / this.step()));
        return Math.max(1, items.length - visible + 1);
      }

      currentPage() {
        return Math.round(this.track.scrollLeft / this.step());
      }

      goTo(page) {
        this.targetPage = page;
        this.track.scrollTo({
          left: page * this.step(),
          behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
        });
      }

      render() {
        const pages = this.mobile.matches ? this.pageCount() : 0;
        this.dots.hidden = pages < 2;
        this.dots.replaceChildren(
          ...Array.from({ length: pages }, (_, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'movrano-logo-list__dot';
            dot.setAttribute('aria-label', `Logo-Gruppe ${i + 1} von ${pages}`);
            dot.addEventListener('click', () => this.goTo(i));
            return dot;
          })
        );
        this.markActive();
      }

      markActive() {
        const page = this.currentPage();
        this.dots
          .querySelectorAll('button')
          .forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
      }

      startAutoplay() {
        const delay = Number(this.dataset.autoplay);
        if (!delay) return;
        this.timer = setInterval(() => {
          if (this.paused || !this.mobile.matches || this.reduceMotion.matches || document.hidden) return;
          const pages = this.pageCount();
          if (pages < 2) return;
          const current = this.targetPage ?? this.currentPage();
          this.goTo((current + 1) % pages);
        }, delay);
      }
    }
  );
}

// Bildvergleich: Reglerwert in die CSS-Variable --mv-pos übertragen.
if (!customElements.get('movrano-compare')) {
  customElements.define(
    'movrano-compare',
    class MovranoCompare extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.movrano-compare__input');
        if (!this.input) return;
        this.onInput = () => this.style.setProperty('--mv-pos', `${this.input.value}%`);
        this.input.addEventListener('input', this.onInput);
        this.onInput();
      }

      disconnectedCallback() {
        this.input?.removeEventListener('input', this.onInput);
      }
    }
  );
}

// Bildergalerie: Pfeile und Punkte für einen wischbaren Slider (Scroll-Snap).
if (!customElements.get('movrano-gallery')) {
  customElements.define(
    'movrano-gallery',
    class MovranoGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.movrano-gallery__track');
        this.dots = this.querySelector('.movrano-gallery__dots');
        this.prev = this.querySelector('[data-mv-prev]');
        this.next = this.querySelector('[data-mv-next]');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.render = this.render.bind(this);
        this.onScroll = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.markActive());
        };
        this.onPrev = () => this.goTo(this.currentPage() - 1);
        this.onNext = () => this.goTo(this.currentPage() + 1);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev?.addEventListener('click', this.onPrev);
        this.next?.addEventListener('click', this.onNext);
        this.resizeObserver = new ResizeObserver(this.render);
        this.resizeObserver.observe(this.track);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      items() {
        return Array.from(this.track.children);
      }

      step() {
        const items = this.items();
        return items[1] ? items[1].offsetLeft - items[0].offsetLeft : items[0]?.offsetWidth || 1;
      }

      // Anzahl möglicher Startpositionen (bei fünf sichtbaren Bildern: Bilder − 4)
      pageCount() {
        const items = this.items();
        if (!items.length) return 0;
        const visible = Math.max(1, Math.round((this.track.clientWidth + 1) / this.step()));
        return Math.max(1, items.length - visible + 1);
      }

      currentPage() {
        return Math.round(this.track.scrollLeft / this.step());
      }

      // Am Ende geht es wieder von vorn los (wie in der Referenz)
      goTo(page) {
        const pages = this.pageCount();
        const target = (page + pages) % pages;
        this.track.scrollTo({
          left: target * this.step(),
          behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
        });
      }

      render() {
        const pages = this.pageCount();
        const count = this.dots.children.length;
        [this.prev, this.next].forEach((button) => button && (button.hidden = pages < 2));
        this.dots.hidden = pages < 2;
        if (count !== pages) {
          this.dots.replaceChildren(
            ...Array.from({ length: pages }, (_, i) => {
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'movrano-gallery__dot';
              dot.setAttribute('aria-label', `Galerie-Position ${i + 1} von ${pages}`);
              dot.addEventListener('click', () => this.goTo(i));
              return dot;
            })
          );
        }
        this.markActive();
      }

      markActive() {
        const page = this.currentPage();
        this.dots
          .querySelectorAll('button')
          .forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
      }
    }
  );
}

// Produktbeschreibung am Smartphone einklappen („Weiterlesen“). Ohne JavaScript bleibt der Text vollständig sichtbar.
if (!customElements.get('movrano-readmore')) {
  customElements.define(
    'movrano-readmore',
    class MovranoReadmore extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('.movrano-readmore__content');
        this.button = this.querySelector('.movrano-readmore__toggle');
        if (!this.content || !this.button) return;
        this.label = this.button.querySelector('[data-label]');
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.expanded = false;

        this.update = this.update.bind(this);
        this.onClick = () => this.setExpanded(!this.expanded);
        this.mobile.addEventListener('change', this.update);
        this.button.addEventListener('click', this.onClick);
        this.update();
      }

      disconnectedCallback() {
        this.mobile?.removeEventListener('change', this.update);
      }

      // Nur einklappen, wenn sich das lohnt (deutlich länger als der sichtbare Anfang)
      update() {
        const limit = parseFloat(getComputedStyle(this).getPropertyValue('--mv-readmore-height')) * 10 || 260;
        const worth = this.mobile.matches && this.content.scrollHeight > limit + 120;
        this.button.hidden = !worth;
        this.classList.toggle('is-collapsed', worth && !this.expanded);
      }

      setExpanded(expanded) {
        this.expanded = expanded;
        this.classList.toggle('is-collapsed', !expanded);
        this.button.setAttribute('aria-expanded', String(expanded));
        this.label.textContent = expanded ? this.button.dataset.labelLess : this.button.dataset.labelMore;
        // Beim Zuklappen zurück zum Anfang der Beschreibung, unterhalb der feststehenden Kopfzeile
        const header = document.querySelector('.section-header');
        const offset = (header?.getBoundingClientRect().bottom || 0) + 16;
        const top = this.getBoundingClientRect().top;
        if (!expanded && top < offset) {
          window.scrollTo({ top: window.scrollY + top - offset, behavior: 'auto' });
        }
      }
    }
  );
}

/* Gratis-Versand-Leiste (snippets/movrano-free-shipping.liquid): „Zweite Bürste hinzufügen“ */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-mvp-ship-add]');
  if (!button) return;
  button.disabled = true;
  const drawer = document.querySelector('cart-drawer');
  const onCartPage = /\/cart\/?$/.test(window.location.pathname);
  const root = window.Shopify?.routes?.root || '/';
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
        sections: drawer && !onCartPage ? ['cart-drawer', 'cart-icon-bubble'] : [],
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Fehler');
    if (drawer && !onCartPage && data.sections) {
      drawer.renderContents(data);
    } else {
      window.location.reload();
    }
  } catch (error) {
    window.location.href = `${root}cart`;
  }
});
